<template>
  <property-report-card
    description="View nearby points of interest to the subject."
    title="Points of Interest"
  >
    <q-skeleton
      v-show="(isLoading || !isPoiQueryEnabled) && !printingMode"
      animation="blink"
      class="google-map"
    />

    <google-map
      v-show="(isPoiQueryEnabled && !isLoading) || printingMode"
      ref="poiMapRef"
      :api-key="apiKey"
      :api-promise="apiPromise"
      :center="mapCenter"
      :max-zoom="32"
      :styles="theme"
      class="google-map"
      data-private
      @idle="onBoundsChanged"
    >
      <google-marker
        v-if="subjectDetail"
        :options="{
          position: { lat: subjectDetail.lat, lng: subjectDetail.lon },
          optimized: true,
          icon: {
            url: '/marker-primary.svg',
            labelOrigin: point || undefined
          },
          label: {
            text: subjectStarIcon,
            color: '#6365f1',
            fontWeight: 'bold'
          },
          zIndex: 2
        }"
      >
        <info-window
          ref="poiMapSubjectMarker"
          :options="{ headerDisabled: true }"
          class="text-black"
        >
          <map-property-card
            :can-create-property-reports="canCreatePropertyReports"
            :is-candidate="false"
            :property="subjectDetail"
            :show-image="!errorLoadingImage[subjectDetail.id]"
            @close="closeInfoWindow"
            @image-error="errorLoadingImage[subjectDetail.id] = true"
          />
        </info-window>
      </google-marker>
      <google-marker
        v-for="poi in limitedPointsOfInterest.filter((p) =>
          visibleCategories.includes(p.category!)
        )"
        :key="poi.poi_id"
        :options="{
          position: { lat: poi.latitude!, lng: poi.longitude! },
          optimized: true,
          icon: getPoiIcon(poi)
        }"
        @click="selectedPoi = poi"
      />
      <custom-control position="TOP_RIGHT">
        <div class="map-legend">
          <div class="legend-title">Points of Interest</div>
          <!-- Subject Property Legend Item -->
          <div v-if="subjectDetail" class="legend-item">
            <div class="legend-dot subject-dot">★</div>
            <span class="legend-label">Subject Property</span>
          </div>
          <!-- POI Category Legend Items -->
          <div v-for="category in availableCategories" :key="category" class="legend-item">
            <q-checkbox v-model="visibleCategories" :val="category" class="legend-checkbox" dense />
            <div :style="{ backgroundColor: getCategoryColor(category) }" class="legend-dot"></div>
            <span
              :class="{ 'legend-label-unchecked': !visibleCategories.includes(category) }"
              class="legend-label"
              >{{ formatCategoryName(category) }}</span
            >
            <span
              :class="{ 'legend-label-unchecked': !visibleCategories.includes(category) }"
              class="legend-count-label"
              >({{ poiCountsByCategory[category] || 0 }})</span
            >
          </div>

          <div v-if="availableCategories.length === 0" class="legend-item">
            <span class="legend-label text-grey-7">No points of interest found</span>
          </div>
        </div>
      </custom-control>
      <info-window
        v-if="selectedPoi"
        :options="{
          headerDisabled: true,
          position: {
            lat: selectedPoi.latitude!,
            lng: selectedPoi.longitude!
          }
        }"
        class="text-black"
        style="width: 152px"
      >
        <q-btn
          class="full-width"
          dense
          label="Close"
          no-caps
          outline
          size="xs"
          @click="selectedPoi = null"
        />
        <q-img
          :src="propertyThumbnail(selectedPoi)"
          alt="Building Image"
          class="rounded-borders q-my-xs"
          crossorigin="anonymous"
          data-private
          @error="errorLoadingImage[selectedPoi.poi_id] = true"
        />
        <div class="poi-info-window">
          <div>
            <strong class="poi-name text-black"> {{ selectedPoi.business_name }} </strong>
          </div>
          <div class="poi-industry">
            {{ formatCategoryName(selectedPoi.category || selectedPoi.industry || '') }}
          </div>
          <div class="poi-address">{{ selectedPoi.street }}</div>
          <div class="poi-distance">
            {{ safeMiles(selectedPoi.distance_miles as number) }} mi away
          </div>
        </div>
      </info-window>
    </google-map>
  </property-report-card>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'
import { GoogleMap, Marker as GoogleMarker, InfoWindow, CustomControl } from 'vue3-google-map'
import MapPropertyCard from '@/components/PropertyReports/MapPropertyCard.vue'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import type { PointOfInterest, PropertyDetailsResponse } from 'common'
import { getStreetViewUrl } from 'common'
import apiPromise from '@/setups/googleMaps.ts'
import { usePropertyReportPointsOfInterest } from '@/queries'
import { useUserStore } from '@/stores'
import { POI_CATEGORIES } from '@/constants'
import { darkMapTheme, lightMapTheme } from '@/utils.ts'

export default defineComponent({
  components: {
    PropertyReportCard,
    MapPropertyCard,
    GoogleMap,
    GoogleMarker,
    InfoWindow,
    CustomControl
  },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    comparables: {
      type: Array as PropType<PropertyDetailsResponse[] | null>,
      default: () => []
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      apiPromise: apiPromise as unknown as Promise<typeof google>,
      apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
      poiMapRef: null as null | typeof GoogleMap,
      errorLoadingImage: {} as Record<string, boolean>,
      point: null as google.maps.Point | null,
      selectedPoi: null as PointOfInterest | null,
      visibleCategories: [] as string[],
      limitedPointsOfInterest: [] as PointOfInterest[]
    }
  },
  computed: {
    theme() {
      return this.$q.dark.isActive ? darkMapTheme : lightMapTheme
    },
    subjectStarIcon() {
      return this.printingMode ? ' ' : '★'
    },
    canCreatePropertyReports() {
      return useUserStore().canCreatePropertyReports
    }
  },
  watch: {
    subjectDetail() {
      this.poiSubjectProperty = this.subjectDetail
      this.onLoad()
    },
    comparables() {
      this.poiComparables = this.comparables
      this.onLoad()
    },
    pointsOfInterest() {
      this.onBoundsChanged()
      this.onLoad()
    },
    availableCategories: {
      handler(newCategories) {
        // Initialize all categories as checked when they first become available
        if (this.visibleCategories.length === 0 && newCategories.length > 0) {
          this.visibleCategories = [...newCategories]
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.poiSubjectProperty = this.subjectDetail
    this.poiComparables = this.comparables

    this.isPoiQueryEnabled = true

    this.poiMapRef = this.$refs['poiMapRef'] as typeof GoogleMap

    // Watch for when the map is ready
    this.$watch(
      () => this.poiMapRef?.ready,
      () => {
        this.onLoad()
      }
    )

    this.apiPromise
      .then(() => {
        this.point = new google.maps.Point(16, 17)
      })
      .catch((e) => console.error(e))
  },
  methods: {
    onBoundsChanged() {
      const map = this.poiMapRef?.map
      if (!map) {
        return
      }

      const bounds = map.getBounds()
      if (!bounds) {
        return
      }

      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()

      const neLat = ne.lat()
      const neLon = ne.lng()
      const swLat = sw.lat()
      const swLon = sw.lng()

      this.limitedPointsOfInterest = this.pointsOfInterest
        .filter((p) => {
          const lat = p.latitude as number
          const lon = p.longitude as number
          return lat >= swLat && lat <= neLat && lon >= swLon && lon <= neLon
        })
        .slice(0, 5000)
    },
    propertyThumbnail(poi: PointOfInterest) {
      if (poi.street && poi.city && poi.state_name && poi.zip) {
        return getStreetViewUrl(
          { street_address: poi.street, city: poi.city, state: poi.state_name, zip_code: poi.zip },
          import.meta.env.VITE_API_URL
        )
      }
      return ''
    },
    closeInfoWindow() {
      const closeRefs = (refOrArray: any) => {
        if (!refOrArray) {
          return
        }
        const refs = Array.isArray(refOrArray) ? refOrArray : [refOrArray]
        for (const ref of refs) {
          if (ref && typeof ref.close === 'function') {
            ref.close()
          }
        }
      }

      // Close subject property info window
      closeRefs(this.$refs['poiMapSubjectMarker'])
    },
    getPoiIcon(poi: PointOfInterest) {
      const categoryInfo = POI_CATEGORIES[poi.category ?? '']

      return {
        url: `/poi-markers/${categoryInfo?.icon ?? 'fallback-marker.svg'}`,
        scaledSize: new google.maps.Size(10, 10),
        anchor: new google.maps.Point(5, 5)
      }
    },
    safeMiles(distance: number | null | undefined): string {
      if (distance == null || isNaN(distance)) {
        return '-'
      }
      return distance.toFixed(2)
    },
    formatCategoryName(category: string): string {
      return category
        .split(' - ')
        .map((part) =>
          part
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        )
        .join(' - ')
    },
    onLoad() {
      if (!this.poiMapRef?.ready || !this.mapCenter) {
        return
      }

      const bounds = new google.maps.LatLngBounds()

      // Adjust bounds to fit subject and all POI's inside
      if (this.subjectDetail) {
        bounds.extend(new google.maps.LatLng(this.subjectDetail.lat, this.subjectDetail.lon))
      }

      for (const poi of this.pointsOfInterest) {
        if (poi.latitude && poi.longitude) {
          bounds.extend(new google.maps.LatLng(poi.latitude, poi.longitude))
        }
      }

      // Fit the map to show all markers
      const gmap = this.poiMapRef.map
      gmap.fitBounds(bounds)

      // Optional: Set a minimum zoom level if you don't want to zoom out too much
      const listener = google.maps.event.addListener(gmap, 'bounds_changed', () => {
        if (gmap.getZoom() && gmap.getZoom()! > 32) {
          gmap.setZoom(32)
        }
        google.maps.event.removeListener(listener)
      })
    }
  },
  setup() {
    const {
      subjectProperty: poiSubjectProperty,
      comparables: poiComparables,
      isEnabled: isPoiQueryEnabled,
      mapCenter,
      radiusMiles,
      pointsOfInterest,
      isLoading
    } = usePropertyReportPointsOfInterest()

    function getCategoryColor(category: string): string {
      return POI_CATEGORIES[category]?.color ?? '#808080'
    }

    const availableCategories = computed(() => {
      const categories = new Set<string>()
      pointsOfInterest.value?.forEach((poi) => {
        if (poi.category) {
          categories.add(poi.category)
        }
      })
      return Array.from(categories).sort()
    })

    const poiCountsByCategory = computed(() => {
      const counts: Record<string, number> = {}
      pointsOfInterest.value?.forEach((poi) => {
        if (poi.category) {
          counts[poi.category] = (counts[poi.category] || 0) + 1
        }
      })
      return counts
    })

    return {
      isPoiQueryEnabled,
      poiSubjectProperty,
      poiComparables,
      mapCenter,
      radiusMiles,
      pointsOfInterest,
      isLoading,
      getCategoryColor,
      availableCategories,
      poiCountsByCategory
    }
  }
})
</script>

<style lang="scss" scoped>
@media print {
  .google-map {
    width: 50% !important;
  }
}
.google-map {
  width: 100%;
  min-height: 300px;
  height: calc(100vh / 2);
}
.metric-label {
  font-size: 0.8em;
  font-weight: 400;
  color: #6c757d;
}

.map-legend {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 12px;
  margin: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  max-width: 300px;
  backdrop-filter: blur(4px);
}

.legend-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 4px;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  gap: 4px;

  &:last-child {
    margin-bottom: 0;
  }
}

.legend-checkbox {
  margin-right: 2px;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-left: 4px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.8);
}

.subject-dot {
  background: #6365f1;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: bold;
  border: 1px solid #6365f1;
}

.legend-label {
  font-size: 12px;
  color: #333;
  line-height: 1.2;
}

.legend-label-unchecked {
  text-decoration: line-through;
  opacity: 0.6;
}

.legend-count-label {
  display: inline-block;
  font-size: 12px;
  color: #555;
}

/* Dark mode support */
.q-dark .map-legend {
  background: rgba(30, 30, 30, 0.95);
  color: #fff;
}

.q-dark .legend-title {
  color: #fff;
  border-bottom-color: #555;
}

.q-dark .legend-label {
  color: #fff;
}

.q-dark .legend-count-label {
  color: #989898;
}

.poi-info-window {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 12px;
  line-height: 1.4;
  max-width: 220px;
}

.poi-info-window .poi-name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}

.poi-info-window .poi-industry {
  font-size: 11px;
  font-weight: 500;
  color: #4b5563;
}

.poi-info-window .poi-address {
  font-size: 11px;
  color: #6b7280;
}

.poi-info-window .poi-distance {
  font-size: 11px;
  font-weight: 500;
  color: #6366f1;
}

.q-dark .poi-info-window .poi-name {
  color: #f9fafb;
}

.q-dark .poi-info-window .poi-industry,
.q-dark .poi-info-window .poi-address {
  color: #9ca3af;
}

.q-dark .poi-info-window .poi-distance {
  color: #a5b4fc;
}
</style>
